import { useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export function AlbaranEditor({ initial, onClose, onSaved, baseCurrency, lang }) {
  // Assign a stable client-side _uid to every line so React's key stays
  // stable across add/remove (using array index as key caused focus/state
  // glitches when the user deleted a middle row).
  const seedWithUid = (obj) => ({
    ...obj,
    line_items: (obj.line_items || []).map((li) => (
      li._uid ? li : { ...li, _uid: crypto.randomUUID() }
    )),
  });
  const [d, setD] = useState(() => seedWithUid(initial));
  const [saving, setSaving] = useState(false);
  const isNew = !initial.id;
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const setLine = (idx, k, v) =>
    setD((x) => ({ ...x, line_items: x.line_items.map((li, i) => i === idx ? { ...li, [k]: v } : li) }));
  const addLine = () => setD((x) => ({ ...x,
    line_items: [...(x.line_items || []), { _uid: crypto.randomUUID(), name: "", qty: 1, unit_price: 0, total: 0 }] }));
  const removeLine = (idx) => setD((x) => ({ ...x, line_items: x.line_items.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        vendor: d.vendor || null,
        vendor_tax_id: d.vendor_tax_id || null,
        date: d.date || null,
        albaran_number: d.albaran_number || null,
        total: parseFloat(d.total) || 0,
        subtotal: d.subtotal != null && d.subtotal !== "" ? parseFloat(d.subtotal) : null,
        tax: d.tax != null && d.tax !== "" ? parseFloat(d.tax) : null,
        currency: d.currency || baseCurrency,
        notes: d.notes || null,
        line_items: (d.line_items || [])
          .filter((li) => (li.name || "").trim())
          .map((li) => ({
            name: li.name.trim(),
            qty: parseFloat(li.qty) || 1,
            unit_price: li.unit_price != null && li.unit_price !== "" ? parseFloat(li.unit_price) : null,
            total: li.total != null && li.total !== "" ? parseFloat(li.total) : null,
          })),
      };
      if (isNew) await api.post("/albaranes", payload);
      else await api.put(`/albaranes/${d.id}`, payload);
      toast.success(lang === "es" ? "Guardado" : "Saved");
      onSaved(); onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="albaran-editor">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? (lang === "es" ? "Nuevo albarán" : "New delivery note")
              : (lang === "es" ? "Editar albarán" : "Edit delivery note")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Proveedor" : "Vendor"}</Label>
              <Input value={d.vendor || ""} onChange={(e) => set("vendor", e.target.value)} data-testid="alb-vendor" className="h-9" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Fecha" : "Date"}</Label>
              <Input type="date" value={d.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="alb-date" className="h-9" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Nº albarán" : "Note #"}</Label>
              <Input value={d.albaran_number || ""} onChange={(e) => set("albaran_number", e.target.value)} data-testid="alb-number" className="h-9" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Total</Label>
              <Input type="number" step="0.01" value={d.total ?? ""} onChange={(e) => set("total", e.target.value)} data-testid="alb-total" className="h-9 font-mono-num" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Moneda" : "Currency"}</Label>
              <Input value={d.currency || baseCurrency} onChange={(e) => set("currency", e.target.value.toUpperCase())} className="h-9 uppercase" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">CIF/NIF</Label>
              <Input value={d.vendor_tax_id || ""} onChange={(e) => set("vendor_tax_id", e.target.value.toUpperCase())} className="h-9 uppercase" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-wider">{lang === "es" ? "Líneas" : "Lines"}</Label>
              <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="alb-add-line">
                <Plus size={12} className="mr-1" />{lang === "es" ? "Añadir" : "Add"}
              </Button>
            </div>
            <div className="space-y-1.5">
              {(d.line_items || []).map((li, idx) => (
                <div key={li._uid || idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`alb-line-${idx}`}>
                  <Input value={li.name} onChange={(e) => setLine(idx, "name", e.target.value.toLowerCase())} placeholder={lang === "es" ? "Producto" : "Product"} className="col-span-5 h-9" />
                  <Input type="number" step="any" value={li.qty ?? ""} onChange={(e) => setLine(idx, "qty", e.target.value)} placeholder="Qty" className="col-span-2 h-9 font-mono-num" />
                  <Input type="number" step="any" value={li.unit_price ?? ""} onChange={(e) => setLine(idx, "unit_price", e.target.value)} placeholder="€/ud" className="col-span-2 h-9 font-mono-num" />
                  <Input type="number" step="any" value={li.total ?? ""} onChange={(e) => setLine(idx, "total", e.target.value)} placeholder="Total" className="col-span-2 h-9 font-mono-num" />
                  <button onClick={() => removeLine(idx)} type="button" className="col-span-1 p-1.5 rounded hover:bg-[var(--surface-hover)] text-[var(--status-negative)]"><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>{lang === "es" ? "Cancelar" : "Cancel"}</Button>
          <Button onClick={save} disabled={saving} className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white" data-testid="alb-save">
            {saving ? "…" : (lang === "es" ? "Guardar" : "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
